import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["buttonType", "portalId", "loginStatus", "hubletOverride", "onSuccess", "onFailure"],
  _excluded2 = ["buttonType", "portalId", "loginStatus", "hubletOverride", "onSuccess", "onFailure"];
import env from 'enviro';
import { buildUrl } from 'hub-http/helpers/url';
import { stringify } from 'hub-http/helpers/params';
import { useEffect, useRef, useState } from 'react';
import { fetchAuthConfigForDataSync as buildAuthConfigFetcherForDataSync } from './DataSync';
import { AuthenticateError, DuplicateDataSyncAccountError, MissingPermissions } from './LowLevelUnifiedAuth';
import { getFullUrl } from 'hubspot-url-utils';
import { buildApiPayload } from './UniversalAuthSteps';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { SelectableButton } from 'foundations-components/transitional/components/SelectableButton';
import { jsx as _jsx } from "react/jsx-runtime";
const googleAppId = env.isQa() ? 1203680 : 232794;
const oAuthRedirectOrigin = `https://oauth-redirect.hubspot${env.isQa() ? 'qa' : ''}.com`;
export function GoogleDataSyncAuthButton(props) {
  const oauthWindowRef = useRef(undefined);
  const oauthStateRef = useRef('');
  const [status, setStatus] = useState('idle');
  useEffect(() => {
    if (status === 'authorizing' && props.loginStatus === 'completed') {
      let timer;
      let heartbeat;
      let messageHandler;
      const fetchAuthConfig = buildAuthConfigFetcherForDataSync(props.portalId, googleAppId, undefined, props.hubletOverride);
      fetchAuthConfig().then(authConfig => {
        timer = setTimeout(() => {
          const oauthWindow = oauthWindowRef.current;
          if (oauthWindow) {
            oauthWindow.close();
            setStatus('failed');
            props.onFailure({
              type: 'OAuthWindowClosed'
            });
          }
        }, 300000);
        heartbeat = setInterval(() => {
          const oauthWindow = oauthWindowRef.current;
          if (!oauthWindow || oauthWindow && oauthWindow.closed) {
            setStatus('failed');
            props.onFailure({
              type: 'OAuthWindowClosed'
            });
          }
        }, 1000);
        messageHandler = event => {
          const oauthWindow = oauthWindowRef.current;
          if (event.data && oauthWindow && event.source === oauthWindow) {
            if ((event.origin === oAuthRedirectOrigin || event.origin === getFullUrl('app')) && event.data === 'ping') {
              oauthWindow.postMessage('pong', event.origin);
            } else if (event.origin === oAuthRedirectOrigin && event.data.type === 'AUTH_RESPONSE') {
              const oauthRedirectSearchParams = new URLSearchParams(event.data.queryString);

              // CSRF protection: https://datatracker.ietf.org/doc/html/rfc6749#section-10.12
              // Verify the validity of the request by matching the received state with the user-agent's authenticated state.
              // We have 3 cases:
              //  1. There's a state parameter and it matches the user-agent's authenticated state
              //  2. The state does not match, the request is invalid and should be rejected
              //  3. There is no state, the request is invalid and should be rejected

              if (oauthRedirectSearchParams.has('state')) {
                // Case 1. There's a state parameter and it matches the user-agent's authenticated state
                if (oauthStateRef.current === oauthRedirectSearchParams.get('state')) {
                  const payload = buildApiPayload(authConfig.authFlow.sessionId, authConfig.authFlow.steps, {}, oauthRedirectSearchParams, oauthStateRef.current, authConfig.oAuthScope);
                  authConfig.finishAuthFlow(payload).then(account => {
                    props.onSuccess(account.id);
                    setStatus('success');
                  }).catch(err => {
                    setStatus('failed');
                    if (err instanceof AuthenticateError || err instanceof DuplicateDataSyncAccountError || err instanceof MissingPermissions) {
                      props.onFailure(err.toAuthError());
                    } else if (err.responseJSON && err.responseJSON.message) {
                      props.onFailure({
                        type: 'AuthenticateError',
                        message: err.responseJSON.message
                      });
                    } else {
                      props.onFailure({
                        type: 'AuthenticateError',
                        message: err.message
                      });
                    }
                  });
                } else {
                  // Case 2: The state does not match, the request is invalid and should be rejected
                  setStatus('failed');
                  props.onFailure({
                    type: 'OAuthWindowClosed'
                  });
                }
              } else {
                // Case 3: There is no state, the request is invalid and should be rejected
                setStatus('failed');
                props.onFailure({
                  type: 'OAuthWindowClosed'
                });
              }

              // TODO: we should send case 2 & 3 to the UnifiedAuth BE in the future so we can track them and present a nice error message.
              clearTimeout(timer);
              clearInterval(heartbeat);
              oauthWindow.close();
              oauthStateRef.current = '';
            }
          }
        };
        window.addEventListener('message', messageHandler);
        const oAuthState = authConfig.authFlow.sessionId;
        oauthStateRef.current = oAuthState;
        const authorizeUrl = buildUrl(Object.assign({}, authConfig.makeAuthorizeUrlDescriptor(), {
          query: stringify({
            sessionId: authConfig.authFlow.sessionId,
            scope: authConfig.oAuthScope,
            state: oAuthState,
            portalId: props.portalId
          })
        }));
        const oauthWindow = oauthWindowRef.current;
        if (oauthWindow && !oauthWindow.closed) {
          oauthWindow.location = authorizeUrl;
        } else {
          setStatus('failed');
          props.onFailure({
            type: 'OAuthWindowClosed'
          });
        }
      }).catch(() => {
        props.onFailure({
          type: 'AuthenticateError'
        });
        setStatus('failed');
        const oauthWindow = oauthWindowRef.current;
        if (oauthWindow) oauthWindow.close();
      });
      return () => {
        if (timer) clearTimeout(timer);
        if (heartbeat) clearInterval(heartbeat);
        if (messageHandler) window.removeEventListener('message', messageHandler);
      };
    }
    if (props.loginStatus === 'failed') {
      const oauthWindow = oauthWindowRef.current;
      if (oauthWindow) {
        oauthWindow.close();
        props.onFailure({
          type: 'OAuthWindowClosed'
        });
      }
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, props.loginStatus]);
  const onClick = event => {
    const width = 620;
    const height = 790;
    const {
      screenY,
      screenX,
      outerHeight,
      outerWidth
    } = window;

    // calculate top and left in order to center the window on the users' screen
    const top = Math.round(screenY + Math.max(0, Math.floor(outerHeight - height) / 2));
    const left = Math.round(screenX + Math.max(0, Math.floor(outerWidth - width) / 2));
    const oAuthWindow = window.open('about:blank', '_blank', `menubar=no,location=no,resizable=yes,scrollbars=yes,status=no,titlebar=no,toolbar=no,width=${width},height=${height},top=${top},left=${left}`);
    if (oAuthWindow) {
      oauthWindowRef.current = oAuthWindow;
      setStatus('authorizing');
      if ('onClick' in props && props.onClick) {
        props.onClick(event);
      }
    } else {
      setStatus('failed');
      props.onFailure({
        type: 'OAuthWindowBlocked'
      });
    }
  };
  if (props.buttonType === 'loading') {
    const loadingButtonProps = _objectWithoutPropertiesLoose(props, _excluded);
    return /*#__PURE__*/_jsx(LoadingButton, Object.assign({}, loadingButtonProps, {
      loading: status === 'authorizing',
      failed: status === 'failed',
      onClick: onClick,
      children: props.children
    }));
  }
  const selectableButtonProps = _objectWithoutPropertiesLoose(props, _excluded2);
  return /*#__PURE__*/_jsx(SelectableButton, Object.assign({}, selectableButtonProps, {
    onClick: onClick,
    children: props.children
  }));
}